import React, { useEffect, useState , ChangeEvent } from "react";
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, makeStyles, Paper, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Theme, Typography } from "@material-ui/core";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { updateIPWhitelisting } from "../../redux/actions";
import BackdropLoader from "../../../../components/loader/BackdropLoader";
import { useTranslation } from "react-i18next";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import { GeneralStyledTableCell  as StyledTableCell } from "../../../../components/styledtabelcell/StyledTabelCell";
import Alert from '@material-ui/lab/Alert';
import { CustomColors } from "../../../../constants/enums";
import palette from "../../../../theme/palette";

const useStyles = makeStyles((theme: Theme) => ({
  // headerWrap: {
  //   display: "flex",
  //   justifyContent: "space-between",
  //   alignItems: "center",
  //   paddingLeft: 8,
  //   paddingRight: 8,
  // },
  onEditActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 10,
  },
  topmargin: {
    marginTop: 44,
  },
  subHeadingText:{
    color: palette.text.disabled
  },
  infoBackground:{
    borderRadius: '8px', 
    backgroundColor: CustomColors.Info.Lighter
  },
  infoText:{
      color: CustomColors.Info.Darker
  },
  infoPaper: {
      margin: "20px 20px",
  },
  colWidth:{
    width: '50%'
  }
  }));

interface Props extends PropsFromRedux {}

export const IpWhitelisting = (props: Props) => {
  const classes = useStyles();
  const [t] = useTranslation()
  const [chips, setChips] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const isValues = (props.environmentDetails.whitelisted_ips || "").split(",");

  const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/[0-9]+)?$/;

  const handleInputChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const newChips = [...chips];
    newChips[index] = value;
    let newErrors: ("Invalid IP address format." | "Duplicate IP address." | "")[] = newChips.map((chip) => {
      const trimmed = chip.trim();
      if (trimmed && !ipRegex.test(trimmed)) {
        return "Invalid IP address format.";
      }
      return "";
    });
    const normalizedChips = newChips.map(c => c.trim());
    const counts = normalizedChips.reduce((acc: Record<string, number[]>, chip, i) => {
      if (chip) {
        if (!acc[chip]) acc[chip] = [];
        acc[chip].push(i);
      }
      return acc;
    }, {});

    Object.values(counts).forEach(indices => {
      if (indices.length > 1) {
        const [, ...duplicates] = indices;
        duplicates.forEach(i => {
          if (!newErrors[i]) {
            newErrors[i] = "Duplicate IP address.";
          }
        });
      }
    });

    setChips(newChips);
    setErrors(newErrors);
  };

  const handleDeleteChip = (indexToDelete: number) => {
    const updatedChips = chips.filter((_, i) => i !== indexToDelete);

    const updatedErrors: ("Invalid IP address format." | "Duplicate IP address." | "")[] =
      updatedChips.map((chip) => {
        const trimmed = chip.trim();
        if (trimmed && !ipRegex.test(trimmed)) {
          return "Invalid IP address format.";
        }
        return "";
      });

    const normalizedChips = updatedChips.map(c => c.trim());
    const counts = normalizedChips.reduce((acc: Record<string, number[]>, chip, i) => {
      if (chip) {
        if (!acc[chip]) acc[chip] = [];
        acc[chip].push(i);
      }
      return acc;
    }, {});

    Object.values(counts).forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(i => {
          if (!updatedErrors[i]) {
            updatedErrors[i] = "Duplicate IP address.";
          }
        });
      }
    });

    setChips(updatedChips);
    setErrors(updatedErrors);
  };

  const handleEdit = () => {
    setIsEdit(true);
  }

  const handleSubmit = () => {
    const filteredChips = chips.filter(chip => chip.trim() !== "");
    setIsEdit(false);
    const id = props.environmentDetails.id;
    const jsonBody = filteredChips.join(',');
    props.updateIPWhitelisting(id, jsonBody);
    setChips([]);
  };
  

  useEffect(() => {
    if (!props.isIPWhitelisting && props.environmentDetails.whitelisted_ips) {
      const values = props.environmentDetails.whitelisted_ips.split(",");
      setChips(values);
      setErrors(values.map(() => ""));
    }
  }, [props.isIPWhitelisting, props.environmentDetails]);
  

  const arraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  const isChipsEqualToProps = arraysEqual(chips, isValues);

  const handleCancel = () => {
    const ips = props.environmentDetails.whitelisted_ips;
    if (ips) { 
        const values = ips.split(",");
        setChips(values);
    } else {
        setChips([]); 
    }
    setIsEdit(false);
  }
  const handleAddNew = () => {
    setChips([...chips, ""])
    setErrors([...errors, ""]);
  }

  const isEmpty = (chips.length === 0 && (!props.environmentDetails.whitelisted_ips || props.environmentDetails.whitelisted_ips.length === 0));

  const hasEmptyChips = chips.some(chip => chip.trim() === "");

  const hasDuplicateChips = (() => {
    const nonEmpty = chips.map(c => c.trim()).filter(c => c !== "");
    return new Set(nonEmpty).size !== nonEmpty.length;
  })();

  const hasErrors = errors.some(error => !!error);

  return (
    <div data-test="Ipwhitelisting-container">
      {!props.isIPWhitelisting && (<div>
        <Card className="network">
          <CardHeader
            title={<Typography variant="h6">{t('Environment.SettingsTab.ipaddress')}</Typography>}
            data-test="env-network-cardHeader"
            subheader={<Typography variant="caption" className={classes.subHeadingText}>{props.environmentDetails.whitelisted_ips?.length === 0 ? t('Environment.SettingsTab.ipAddressSubTitle1') : t('Environment.SettingsTab.ipAddressSubTitle2')}</Typography>}
            action={
              <>
                {!isEdit ? (
                      <>
                        <IconButton onClick={handleEdit} color="primary" data-test="enable-edit">
                          <EditIcon />
                        </IconButton>
                      </>
                  ) : null}
                  {isEdit ? (
                    <div className={classes.onEditActions}>
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        disabled={isChipsEqualToProps || isEmpty || hasEmptyChips || hasDuplicateChips || hasErrors}
                        onClick={handleSubmit}
                        data-test="update-button"
                      >
                        {t('Environment.SettingsTab.submit')}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        disableElevation={true}
                        onClick={handleCancel}
                        data-test="cancel-button"
                      >
                        {t('Environment.SettingsTab.cancel')}
                      </Button>
                    </div>
                  ) : null}
              </>
            }
          />
          <Divider/>
          <CardContent>
            {isEdit && (
              <Box display="flex" justifyContent="end">
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation={true}
                  onClick={handleAddNew}
                  data-test="add-new-row"
                  disabled={hasDuplicateChips}
                >
                 {t('Environment.SettingsTab.addiplist')}
                </Button>
            </Box>)}
            {chips.length === 0 && !isEdit  ?
                (
                  <Grid container justifyContent="center" className="m-t-10">
                    <Grid item xs={12} sm={11}>
                      <Paper elevation={8}>
                        <Alert severity="info" className={classes.infoBackground}>
                            <Typography variant='body2' className={classes.infoText}>{t("Environment.SettingsTab.ipNoData")}</Typography>
                        </Alert>
                      </Paper>
                    </Grid>
                  </Grid>
                ) :
                (
                  <TableContainer className={classes.topmargin}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell className={classes.colWidth}>
                            {t('Environment.SettingsTab.iplist')}
                          </StyledTableCell>
                          {isEdit && (
                            <StyledTableCell className={classes.colWidth}>
                              {t("Environment.Generals.actions")}
                            </StyledTableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chips.length > 0 &&
                          chips.map((item, index) => (
                            <TableRow key={index}>
                              <StyledTableCell className={classes.colWidth}>
                                {isEdit ? (
                                  <TextField
                                    data-test="env-ip-key"
                                    value={item}
                                    variant="outlined"
                                    onChange={(e:any ) => handleInputChange(index, e)}
                                    error={!!errors[index]}
                                    helperText={errors[index]}
                                    style={{width: '60%'}}
                                  />
                                ) : (
                                  <Typography>{item}</Typography>
                                )}
                              </StyledTableCell>
                              {isEdit && (
                                <StyledTableCell className={classes.colWidth}>
                                  <div>
                                    <CloseIcon
                                      onClick={() => handleDeleteChip(index)}
                                      data-test="remove-ip-from-list"
                                    />
                                  </div>
                                </StyledTableCell>
                              )}
                            </TableRow>
                          ))
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) 
            }

          </CardContent>
        </Card>
      </div>)}
      
      {props.isIPWhitelisting && (<BackdropLoader message={"Updating IP..."} data-test="Updating-ipwhitelisting" />)}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  fetchingEnvVar: state.EnvironmentReducer.fetchingEnvVar,
  environmentDetails: state.EnvironmentReducer.environmentDetails,
  ipWhitelistingData: state.EnvironmentReducer.ipWhitelistingData,
  isIPWhitelisting: state.EnvironmentReducer.isIPWhitelisting,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateIPWhitelisting: (id: number, jsonBody: any) => dispatch(updateIPWhitelisting(id, jsonBody))
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(IpWhitelisting);

type PropsFromRedux = ConnectedProps<typeof connector>;